import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { AdjustDialog, TransferDialog } from "@/components/inventory/StockActions";
import { formatINR } from "@/lib/format";
import { Boxes, Warehouse, History, ShoppingCart, Truck, AlertTriangle, PackageX, Plus, ArrowLeftRight } from "lucide-react";

const MOVE_COLOR = (q) => (q >= 0 ? "text-[#0B7C5A]" : "text-[#B91C1C]");
const SO_STATUS = { Draft: "bg-[#EEF0F5] text-[#6B7189]", Confirmed: "bg-[#EAF3FF] text-[#1D4ED8]", "In Fulfilment": "bg-[#FEF3C7] text-[#B45309]", Delivered: "bg-[#DCFCE7] text-[#15803D]", Invoiced: "bg-[#DCFCE7] text-[#15803D]", Cancelled: "bg-[#FEE2E2] text-[#B91C1C]" };
const PO_STATUS = { Draft: "bg-[#EEF0F5] text-[#6B7189]", Sent: "bg-[#EAF3FF] text-[#1D4ED8]", "Partially Received": "bg-[#FEF3C7] text-[#B45309]", Received: "bg-[#DCFCE7] text-[#15803D]", Cancelled: "bg-[#FEE2E2] text-[#B91C1C]" };

const fdate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—");

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-[#6B7189] mb-1"><Icon className="w-4 h-4" style={{ color }} /> {label}</div>
      <div className="text-2xl font-bold text-[#101D51] leading-tight">{value}</div>
      {sub && <div className="text-[11px] text-[#6B7189] mt-0.5">{sub}</div>}
    </Card>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <Card className="p-4" >
      <div className="font-semibold text-[#101D51] mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#101D51]" /> {title}
        {count != null && <span className="text-xs font-normal text-[#6B7189] bg-[#F5F6FA] rounded-full px-2 py-0.5">{count}</span>}
      </div>
      {children}
    </Card>
  );
}

export default function ProductInventory({ productId, product, canEdit }) {
  const { user } = useAuth();
  const isOwner = canEdit ?? (user?.is_super_admin || ["Owner", "Manager"].includes(user?.role));
  const [d, setD] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [adjust, setAdjust] = useState(false);
  const [transfer, setTransfer] = useState(false);
  const load = useCallback(() => { setD(null); api.get(`/inventory/product/${productId}`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, [productId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {}); }, []);
  if (!d) return <Loading />;
  if (d.error) return <Empty title="Inventory unavailable" hint="Enable the Inventory module to track stock for this product." />;
  const r = d.rollup;
  const uom = r.uom || "Nos";
  const defWh = warehouses.find((w) => w.is_default)?.id || warehouses[0]?.id || "";
  const prod = { id: productId, code: product?.code || "", name: product?.name || "", unit: uom, available: r.available };

  return (
    <div className="space-y-4" data-testid="product-inventory">
      {isOwner && (
        <div className="flex flex-wrap gap-2" data-testid="pi-actions">
          <Button onClick={() => setAdjust(true)} className="h-9 bg-[#101D51] hover:bg-[#080852]" data-testid="pi-adjust-btn"><Plus className="w-4 h-4" /> Adjust stock</Button>
          {warehouses.length > 1 && <Button variant="outline" onClick={() => setTransfer(true)} className="h-9" data-testid="pi-transfer-btn"><ArrowLeftRight className="w-4 h-4" /> Transfer</Button>}
        </div>)}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Boxes} label="On hand" value={<>{r.on_hand}<span className="text-sm font-medium text-[#6B7189]"> {uom}</span></>} sub={r.tracked ? `${r.warehouses} warehouse(s) · ${r.bins} bin(s)` : "Not tracked yet"} color="#101D51" />
        <Stat icon={Boxes} label="Available" value={<>{r.available}<span className="text-sm font-medium text-[#6B7189]"> {uom}</span></>} color="#0B7C5A" />
        <Stat icon={Boxes} label="Reserved" value={<>{r.reserved}<span className="text-sm font-medium text-[#6B7189]"> {uom}</span></>} color="#6D28D9" />
        <Stat icon={Warehouse} label="Stock value" value={formatINR(r.stock_value_paise)} sub={`Reorder at ${r.reorder_level || 0}`} color="#101D51" />
      </div>

      {(r.low_stock || r.out_of_stock) && (
        <div className={`flex items-center gap-2 text-sm rounded-xl px-3 py-2 ${r.out_of_stock ? "bg-[#FEE2E2] text-[#B91C1C]" : "bg-[#FEF3C7] text-[#B45309]"}`} data-testid="product-stock-alert">
          {r.out_of_stock ? <PackageX className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {r.out_of_stock ? "Out of stock across all warehouses." : `Low stock — at or below reorder level (${r.reorder_level}).`}
        </div>
      )}

      <Section icon={Warehouse} title="Stock by warehouse & bin" count={d.stock.length}>
        {d.stock.length === 0 ? <p className="text-sm text-[#6B7189]">No stock records yet. Adjust stock or receive a purchase order to start tracking.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="product-stock-table">
              <thead className="text-[#6B7189] text-left"><tr>
                <th className="py-2 pr-3">Warehouse</th><th className="py-2 pr-3">Bin</th>
                <th className="py-2 pr-3 text-right">Available</th><th className="py-2 pr-3 text-right">Reserved</th><th className="py-2 text-right">Reorder</th>
              </tr></thead>
              <tbody>
                {d.stock.map((s) => (
                  <tr key={s.id} className="border-t border-[#E6E8F0]" data-testid={`product-stock-row-${s.id}`}>
                    <td className="py-2 pr-3 font-medium text-[#101D51]">{s.warehouse?.name || "—"}</td>
                    <td className="py-2 pr-3"><span className="inline-flex items-center gap-1 text-[#101D51]"><span className="font-mono text-xs bg-[#F5F6FA] rounded px-1.5 py-0.5">{s.bin?.code || "—"}</span>{s.bin?.is_default && <span className="text-[10px] text-[#1D4ED8]">default</span>}</span></td>
                    <td className="py-2 pr-3 text-right font-semibold">{s.available_quantity} {uom}</td>
                    <td className="py-2 pr-3 text-right text-[#6B7189]">{s.reserved_quantity || 0}</td>
                    <td className="py-2 text-right text-[#6B7189]">{s.reorder_level || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section icon={ShoppingCart} title="Sales order lines" count={d.sales_order_lines.length}>
        {d.sales_order_lines.length === 0 ? <p className="text-sm text-[#6B7189]">This product hasn't appeared on any sales order.</p> : (
          <div className="divide-y divide-[#F0F1F5]" data-testid="product-so-lines">
            {d.sales_order_lines.map((l, i) => (
              <div key={`${l.order_id}-${i}`} className="flex items-center justify-between py-2 gap-3">
                <div className="min-w-0">
                  <Link to={`/orders/${l.order_id}`} className="text-sm font-medium text-[#1D4ED8] hover:underline">{l.number}</Link>
                  <div className="text-[11px] text-[#6B7189] truncate">{l.customer_name || "—"} · {fdate(l.date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#101D51]">{l.quantity} {l.unit} · {formatINR(l.total)}</div>
                  <span className={`text-[10px] rounded-full px-2 py-0.5 ${SO_STATUS[l.status] || "bg-[#F5F6FA] text-[#6B7189]"}`}>{l.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Truck} title="Purchase order lines" count={d.purchase_order_lines.length}>
        {d.purchase_order_lines.length === 0 ? <p className="text-sm text-[#6B7189]">No vendor purchase orders include this product. Raise one from the Purchasing tab.</p> : (
          <div className="divide-y divide-[#F0F1F5]" data-testid="product-po-lines">
            {d.purchase_order_lines.map((l, i) => (
              <div key={`${l.po_id}-${i}`} className="flex items-center justify-between py-2 gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#101D51]">{l.number}</div>
                  <div className="text-[11px] text-[#6B7189] truncate">{l.supplier_name || "—"} → {l.warehouse_name || "—"} · {fdate(l.date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#101D51]">{l.received_qty}/{l.quantity} {l.unit} · {formatINR(l.line_total)}</div>
                  <span className={`text-[10px] rounded-full px-2 py-0.5 ${PO_STATUS[l.status] || "bg-[#F5F6FA] text-[#6B7189]"}`}>{l.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={History} title="Stock movements" count={d.movements.length}>
        {d.movements.length === 0 ? <p className="text-sm text-[#6B7189]">No movements recorded.</p> : (
          <div className="divide-y divide-[#F0F1F5] max-h-96 overflow-y-auto" data-testid="product-movements">
            {d.movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2 gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-[#101D51]">{m.movement_type}</div>
                  <div className="text-[11px] text-[#6B7189] truncate">{m.warehouse?.name || "—"}{m.bin?.code ? ` · ${m.bin.code}` : ""} · {new Date(m.created_at).toLocaleString("en-IN")}{m.notes ? ` · ${m.notes}` : ""}</div>
                </div>
                <span className={`text-sm font-semibold shrink-0 ${MOVE_COLOR(m.quantity)}`}>{m.quantity >= 0 ? "+" : ""}{m.quantity}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {adjust && <AdjustDialog product={prod} wh={defWh} onClose={() => setAdjust(false)} onDone={() => { setAdjust(false); load(); }} />}
      {transfer && <TransferDialog product={prod} warehouses={warehouses} fromWh={defWh} onClose={() => setTransfer(false)} onDone={() => { setTransfer(false); load(); }} />}
    </div>
  );
}
